import { ReadOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Modal, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { useIdempotencyKey } from "../../../../hooks/use-idempotency-key.ts"
import { cn } from "../../../../utils/cn.ts"
import {
  CUSTOM_FOCUS_CODE,
  FOCUS_CODES,
  suggestionFocusCodes
} from "./education-contract.ts"
import { type AskMoreResult } from "./hooks/use-lead-education.ts"
import type {
  Audience,
  EditionView,
  RequestMoreData
} from "./types/education.types.ts"

const { Text } = Typography

const CUSTOM_FOCUS = CUSTOM_FOCUS_CODE
const MAX_TOPICS = 3
const MIN_REQUEST_LENGTH = 3
const MAX_REQUEST_LENGTH = 500

interface RejectionState {
  message: string
  suggestions: string[]
}

interface AskForMoreModalProps {
  open: boolean
  audience: Audience
  onCancel: () => void
  onSubmit: (
    body: RequestMoreData,
    idempotencyKey: string
  ) => Promise<AskMoreResult>
  /** Called once the 202 comes back, edition included (or `null` if the
   *  response somehow didn't carry one) — the caller owns closing the modal
   *  itself (nothing here does), and selecting the new edition so its
   *  in-flight progress is visible. */
  onSuccess: (edition: EditionView | null) => void
}

/**
 * The Education Center — asks for the next edition of a track. Owns the form
 * (up to three focus topics and the request text) and
 * how the three documented failure shapes present; the caller owns the
 * track/audience it's open for and what happens after a 202. No remaining
 * count is shown: the caller only opens it while an edition is left.
 */
export function AskForMoreModal({
  open,
  audience,
  onCancel,
  onSubmit,
  onSuccess
}: AskForMoreModalProps) {
  const { t } = useTranslation()
  const { key, reset } = useIdempotencyKey()
  const [selectedFocus, setSelectedFocus] = useState<string[]>([])
  const [requestText, setRequestText] = useState("")
  const [customTextMissing, setCustomTextMissing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [rejection, setRejection] = useState<RejectionState | null>(null)
  const [inProgress, setInProgress] = useState(false)
  const [limitReached, setLimitReached] = useState(false)
  const [genericError, setGenericError] = useState<string | null>(null)
  // Guards a rapid double click synchronously — `submitting` alone only
  // takes effect once React re-renders, which a same-tick second click can
  // beat (Review Focus #2: exactly one request per session, same key on retry).
  const inFlightRef = useRef(false)
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!open) return
    setSelectedFocus([])
    setRequestText("")
    setCustomTextMissing(false)
    setSubmitting(false)
    setRejection(null)
    setInProgress(false)
    setLimitReached(false)
    setGenericError(null)
    inFlightRef.current = false
    reset()
    // `reset` is deliberately left out of the deps below (it's stable across
    // renders anyway) — the point is this effect fires once per `open`
    // transition, giving one idempotency key per modal session.
  }, [open])

  const trimmed = requestText.trim()
  const isCustom = selectedFocus.includes(CUSTOM_FOCUS)
  const textValidLength =
    trimmed.length >= MIN_REQUEST_LENGTH && trimmed.length <= MAX_REQUEST_LENGTH
  // Custom's missing text is explained on submit rather than by a silently
  // disabled button — a chosen topic always leaves Create pressable.
  const textOk =
    trimmed.length === 0
      ? isCustom || selectedFocus.length > 0
      : textValidLength
  const hasAnyInput = selectedFocus.length > 0 || trimmed.length > 0
  const canSubmit = textOk && hasAnyInput && !submitting
  const atTopicLimit = selectedFocus.length >= MAX_TOPICS
  const showCustomTextError =
    customTextMissing && isCustom && trimmed.length === 0

  const focusLabel = (code: string) =>
    t(`projectEducation.askMore.focus.${code}`, { defaultValue: code })

  // Offered order, not click order, so the request reads the same way the
  // chips do.
  const toggleFocus = (code: string) =>
    setSelectedFocus((current) =>
      current.includes(code)
        ? current.filter((selected) => selected !== code)
        : current.length >= MAX_TOPICS
          ? current
          : FOCUS_CODES[audience].filter(
              (offered) => offered === code || current.includes(offered)
            )
    )

  const renderFocusChip = (code: string) => {
    const pressed = selectedFocus.includes(code)
    const blocked = !pressed && atTopicLimit
    return (
      <button
        key={code}
        type={"button"}
        aria-pressed={pressed}
        disabled={blocked}
        onClick={() => toggleFocus(code)}
        className={cn(
          "rounded-lg border px-4 py-2 text-sm transition-colors",
          pressed
            ? "border-[#2A7D8C] bg-[#2A7D8C]/10 text-[#2A7D8C]"
            : "border-gray-300 bg-white text-gray-800",
          blocked
            ? "cursor-not-allowed opacity-50"
            : !pressed && "hover:border-[#2A7D8C]"
        )}
      >
        {focusLabel(code)}
      </button>
    )
  }

  const handleSubmit = async () => {
    if (!canSubmit || inFlightRef.current) return
    if (isCustom && !textValidLength) {
      setCustomTextMissing(true)
      textAreaRef.current?.focus()
      return
    }
    inFlightRef.current = true
    setSubmitting(true)
    setRejection(null)
    setInProgress(false)
    setLimitReached(false)
    setGenericError(null)

    const body: RequestMoreData = {}
    if (selectedFocus.length) body.focus_codes = selectedFocus
    if (trimmed) body.request_text = trimmed

    const result = await onSubmit(body, key)
    inFlightRef.current = false
    setSubmitting(false)

    if (result.ok) {
      reset()
      onSuccess(result.edition)
      return
    }

    const { error } = result
    // Live testing (fix round 1): closing immediately on a limit-reached
    // response left the PO with no idea why nothing happened — this now
    // behaves like EDITION_IN_PROGRESS (message shown, modal stays open,
    // the user closes it themselves) instead of auto-closing.
    if (error.code === "EDITION_LIMIT_REACHED") {
      setLimitReached(true)
      return
    }
    if (error.code === "EDITION_IN_PROGRESS") {
      setInProgress(true)
      return
    }
    if (error.code === "REQUEST_REJECTED") {
      setRejection({
        // The backend sends one fixed English sentence for every rejection.
        message: t("projectEducation.askMore.errors.outsideScope"),
        suggestions: suggestionFocusCodes(audience, error.suggestions ?? [])
      })
      return
    }
    setGenericError(error.message)
  }

  const handleCancel = () => {
    reset()
    onCancel()
  }

  const handleTextKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      void handleSubmit()
    }
  }

  return (
    <Modal
      open={open}
      title={
        <Flex gap={16} align={"flex-start"} className={"pb-4"}>
          <span
            aria-hidden
            className={
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2A7D8C]/15 text-lg text-[#2A7D8C]"
            }
          >
            <ReadOutlined />
          </span>
          <div>
            <div className={"text-xl font-semibold"}>
              {t(
                audience === "customer"
                  ? "projectEducation.askMore.title.customer"
                  : "projectEducation.askMore.title.salesPerson"
              )}
            </div>
            <Text type={"secondary"} className={"block font-normal"}>
              {t("projectEducation.askMore.subtitle")}
            </Text>
          </div>
        </Flex>
      }
      onCancel={handleCancel}
      destroyOnHidden
      width={640}
      styles={{
        header: {
          borderBottom: "1px solid rgba(5, 5, 5, 0.06)",
          marginBottom: 20
        },
        footer: {
          borderTop: "1px solid rgba(5, 5, 5, 0.06)",
          paddingTop: 16,
          marginTop: 20
        }
      }}
      footer={
        <Flex justify={"flex-end"} gap={12}>
          <Button onClick={handleCancel}>
            {t("projectEducation.askMore.cancel")}
          </Button>
          <Button
            type={"primary"}
            disabled={!canSubmit}
            loading={submitting}
            onClick={() => void handleSubmit()}
          >
            {t("projectEducation.askMore.submit")}
          </Button>
        </Flex>
      }
    >
      <Flex align={"baseline"} gap={8} className={"mb-3"}>
        <Text strong>{t("projectEducation.askMore.helpWith")}</Text>
        <Text type={"secondary"} className={"text-sm"}>
          {t("projectEducation.askMore.topicLimit", { count: MAX_TOPICS })}
        </Text>
      </Flex>

      <Flex wrap gap={10} className={"mb-5"}>
        {FOCUS_CODES[audience].map((code) => renderFocusChip(code))}
      </Flex>

      <div className={"mb-3"}>
        <label htmlFor={"education-ask-more-text"} className={"block mb-2"}>
          <Text strong>{t("projectEducation.askMore.requestLabel")}</Text>
        </label>
        <TextArea
          id={"education-ask-more-text"}
          ref={(instance) => {
            textAreaRef.current = instance?.resizableTextArea?.textArea ?? null
          }}
          value={requestText}
          rows={4}
          maxLength={MAX_REQUEST_LENGTH}
          showCount
          placeholder={t(
            audience === "customer"
              ? "projectEducation.askMore.placeholder.customer"
              : "projectEducation.askMore.placeholder.salesPerson"
          )}
          onChange={(event) => {
            setRequestText(event.target.value)
            setCustomTextMissing(false)
          }}
          onKeyDown={handleTextKeyDown}
          status={
            showCustomTextError || (trimmed.length > 0 && !textValidLength)
              ? "error"
              : undefined
          }
        />
        {showCustomTextError && (
          <Text type={"danger"} className={"text-xs"}>
            {t("projectEducation.askMore.customTextRequired")}
          </Text>
        )}
        {trimmed.length > 0 && !textValidLength && (
          <Text type={"danger"} className={"text-xs"}>
            {t("projectEducation.askMore.requestTooShort")}
          </Text>
        )}
      </div>

      {inProgress && (
        <Alert
          className={"mb-3"}
          type={"warning"}
          showIcon
          message={t("projectEducation.askMore.errors.editionInProgress")}
        />
      )}

      {limitReached && (
        <Alert
          className={"mb-3"}
          type={"warning"}
          showIcon
          message={t("projectEducation.trackView.maximumDepth")}
        />
      )}

      {genericError && (
        <Alert
          className={"mb-3"}
          type={"error"}
          showIcon
          message={genericError}
        />
      )}

      {rejection && (
        <Alert
          className={"mb-3"}
          type={"error"}
          showIcon
          message={t("projectEducation.askMore.errors.rejected", {
            message: rejection.message
          })}
          description={
            rejection.suggestions.length > 0 ? (
              <Flex vertical gap={6} className={"mt-2"}>
                <Text type={"secondary"} className={"text-xs"}>
                  {t("projectEducation.askMore.suggestionsLabel")}
                </Text>
                <Flex wrap gap={6}>
                  {rejection.suggestions.map((code) => renderFocusChip(code))}
                </Flex>
              </Flex>
            ) : undefined
          }
        />
      )}

      <Text type={"secondary"} className={"block text-sm"}>
        {t("projectEducation.askMore.note")}
      </Text>
    </Modal>
  )
}
